import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Bookmark, MessageCircle, ChevronLeft, ChevronRight, Sparkles, Info, Columns2, X, CheckCircle2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import StudyPanel from "@/components/StudyPanel";
import AudioBiblePlayer from "@/components/AudioBiblePlayer";

export default function Reader() {
  const { lang, version, setVersion } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [bookId, setBookId] = useState(parseInt(searchParams.get("b") || "43"));
  const [chapter, setChapter] = useState(parseInt(searchParams.get("c") || "3"));
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [reflectionOpen, setReflectionOpen] = useState(false);
  const [reflectionText, setReflectionText] = useState("");
  const [studyOpen, setStudyOpen] = useState(false);
  const [studyVerse, setStudyVerse] = useState(null);
  const [posting, setPosting] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareVersion, setCompareVersion] = useState(() => {
    const stored = localStorage.getItem("lumen_compare_version");
    if (stored && stored !== "NVI") return stored;
    return version === "KJV" ? "RV1960" : "KJV";
  });
  const [compareVerses, setCompareVerses] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);

  const currentBook = useMemo(() => books.find((b) => b.bookid === bookId), [books, bookId]);
  const highlightVerse = parseInt(searchParams.get("v") || "0");

  // Reading plan context (?plan=<id>&day=<n>) — enables the "Mark day as read" CTA
  const planId = searchParams.get("plan") || "";
  const planDay = parseInt(searchParams.get("day") || "0");
  const [planInfo, setPlanInfo] = useState(null); // { name_en, name_es, duration_days }
  const [dayCompleted, setDayCompleted] = useState(false);
  const [markingDay, setMarkingDay] = useState(false);

  useEffect(() => {
    if (!planId || !planDay) {
      setPlanInfo(null);
      setDayCompleted(false);
      return;
    }
    (async () => {
      try {
        const [plansRes, progRes] = await Promise.all([
          api.get("/plans"),
          api.get(`/plans/${planId}/progress`),
        ]);
        const pl = (plansRes.data || []).find((p) => p.plan_id === planId);
        setPlanInfo(pl || null);
        setDayCompleted((progRes.data?.completed_days || []).includes(planDay));
      } catch {
        setPlanInfo(null);
      }
    })();
  }, [planId, planDay]);

  const markDayComplete = async () => {
    if (!planId || !planDay || dayCompleted || markingDay) return;
    setMarkingDay(true);
    try {
      const res = await api.post(`/plans/${planId}/progress`, { day: planDay, completed: true });
      const done = (res.data?.completed_days || []).includes(planDay);
      setDayCompleted(done);
      if (done) {
        toast.success(lang === "es" ? `Día ${planDay} completado ✓` : `Day ${planDay} completed ✓`);
      }
    } catch {
      toast.error(lang === "es" ? "No se pudo guardar el progreso" : "Could not save progress");
    } finally {
      setMarkingDay(false);
    }
  };

  const fetchBookmarks = useCallback(async () => {
    try {
      const res = await api.get("/bookmarks");
      setBookmarks(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => { fetchBookmarks(); }, [fetchBookmarks]);

  useEffect(() => {
    (async () => {
      let res;
      try {
        res = await api.get("/bible/books", { params: { translation: version } });
      } catch {
        toast.error(lang === "es" ? "La Biblia está ocupada, intenta de nuevo en unos segundos." : "Bible source is busy, please retry in a few seconds.");
        return;
      }
      setBooks(res.data);
      // If URL has ref_name (from Studies deep-link), resolve to book id
      const refName = searchParams.get("ref_name");
      if (refName) {
        const book = res.data.find(
          (b) => b.name.toLowerCase() === refName.toLowerCase()
        );
        if (book) {
          const c = parseInt(searchParams.get("c") || "1");
          const v = searchParams.get("v") || "";
          setBookId(book.bookid);
          setChapter(c);
          setSearchParams({ b: String(book.bookid), c: String(c), ...(v ? { v } : {}) }, { replace: true });
        }
      }
    })();
    // eslint-disable-next-line
  }, [version]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/bible/chapter", {
          params: { translation: version, book: bookId, chapter },
        });
        setVerses(res.data);
        setSearchParams({ b: String(bookId), c: String(chapter), ...(highlightVerse ? { v: String(highlightVerse) } : {}) });
      } catch {
        toast.error(lang === "es" ? "No se pudo cargar el capítulo. Intenta de nuevo." : "Could not load the chapter. Please retry.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [version, bookId, chapter]);

  // Compare pane: fetch verses when open
  useEffect(() => {
    if (!compareOpen) return;
    localStorage.setItem("lumen_compare_version", compareVersion);
    (async () => {
      setCompareLoading(true);
      try {
        const res = await api.get("/bible/chapter", {
          params: { translation: compareVersion, book: bookId, chapter },
        });
        setCompareVerses(res.data);
      } catch {
        setCompareVerses([]);
      } finally {
        setCompareLoading(false);
      }
    })();
  }, [compareOpen, compareVersion, bookId, chapter]);

  const totalChapters = currentBook?.chapters || 1;

  const bookmarkKey = (v) => `${version}-${bookId}-${chapter}-${v}`;
  const bookmarkedSet = useMemo(
    () => new Set(bookmarks.map((b) => `${b.translation}-${b.book}-${b.chapter}-${b.verse}`)),
    [bookmarks]
  );

  const toggleBookmark = async (verse) => {
    const key = bookmarkKey(verse.verse);
    const existing = bookmarks.find((b) => `${b.translation}-${b.book}-${b.chapter}-${b.verse}` === key);
    if (existing) {
      await api.delete(`/bookmarks/${existing.bookmark_id}`);
      toast.success(t("bookmarked", lang) + " ✕");
    } else {
      await api.post("/bookmarks", {
        translation: version,
        book: bookId,
        book_name: currentBook?.name,
        chapter,
        verse: verse.verse,
        text: verse.text,
      });
      toast.success(t("bookmarked", lang));
    }
    fetchBookmarks();
  };

  const openReflection = (verse) => {
    setSelectedVerse(verse);
    setReflectionText("");
    setReflectionOpen(true);
  };

  const submitReflection = async () => {
    if (!reflectionText.trim() || !selectedVerse) return;
    setPosting(true);
    try {
      await api.post("/community/posts", {
        translation: version,
        book: bookId,
        book_name: currentBook?.name,
        chapter,
        verse: selectedVerse.verse,
        verse_text: selectedVerse.text,
        reflection: reflectionText.trim(),
      });
      toast.success(lang === "es" ? "Reflexión publicada" : "Reflection posted");
      setReflectionOpen(false);
    } finally {
      setPosting(false);
    }
  };

  const testaments = { OT: books.filter((b) => b.testament === "OT"), NT: books.filter((b) => b.testament === "NT") };

  return (
    <div>
      <div data-testid="reader-sticky-toolbar" className="sticky top-16 z-30 -mx-6 md:-mx-8 px-4 md:px-8 py-3 mb-8 bg-[var(--bg)]/80 backdrop-blur-2xl backdrop-saturate-150 border-b border-[var(--border)]/60">
      <div className="flex flex-nowrap items-center gap-1.5 md:gap-3 overflow-x-auto no-scrollbar">
        <Select value={version} onValueChange={setVersion}>
          <SelectTrigger data-testid="version-select" className="w-[72px] md:w-[160px] shrink-0 h-10 px-2.5 md:px-3 bg-[var(--surface)] border-[var(--border)] rounded-full">
            <SelectValue>{version}</SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-[var(--surface-elevated)]">
            <SelectItem value="KJV">KJV · English</SelectItem>
            <SelectItem value="WEB">WEB · English</SelectItem>
            <SelectItem value="NIV">NIV · English</SelectItem>
            <SelectItem value="NLT">NLT · English</SelectItem>
            <SelectItem value="RV1960">RV 1960 · Español</SelectItem>
            <SelectItem value="RV2004">RVG 2004 · Español</SelectItem>
            <SelectItem value="NTV">NTV · Español</SelectItem>
            <SelectItem value="PDT">PDT · Español</SelectItem>
            <SelectItem value="LBLA">LBLA · Español</SelectItem>
          </SelectContent>
        </Select>

        <Select value={String(bookId)} onValueChange={(v) => { setBookId(parseInt(v)); setChapter(1); }}>
          <SelectTrigger data-testid="book-select" className="w-[100px] md:w-[220px] shrink-0 h-10 px-2.5 md:px-3 bg-[var(--surface)] border-[var(--border)] rounded-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[var(--surface-elevated)] max-h-[400px]">
            {testaments.OT.length > 0 && (
              <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">{t("ot", lang)}</div>
            )}
            {testaments.OT.map((b) => (
              <SelectItem key={b.bookid} value={String(b.bookid)}>{b.name}</SelectItem>
            ))}
            {testaments.NT.length > 0 && (
              <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-2">{t("nt", lang)}</div>
            )}
            {testaments.NT.map((b) => (
              <SelectItem key={b.bookid} value={String(b.bookid)}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={String(chapter)} onValueChange={(v) => setChapter(parseInt(v))}>
          <SelectTrigger data-testid="chapter-select" className="w-[58px] md:w-[120px] shrink-0 h-10 px-2.5 md:px-3 bg-[var(--surface)] border-[var(--border)] rounded-full">
            <SelectValue><span className="md:hidden">{chapter}</span><span className="hidden md:inline">{t("chapter", lang)} {chapter}</span></SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-[var(--surface-elevated)] max-h-[400px]">
            {Array.from({ length: totalChapters }, (_, i) => i + 1).map((c) => (
              <SelectItem key={c} value={String(c)}>{t("chapter", lang)} {c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value="" onValueChange={(v) => document.querySelector(`[data-testid="verse-${v}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" })}>
          <SelectTrigger data-testid="verse-jump-select" className="w-[60px] md:w-[110px] shrink-0 h-10 px-2.5 md:px-3 bg-[var(--surface)] border-[var(--border)] rounded-full">
            <SelectValue placeholder={lang === "es" ? "Vers." : "Vs."} />
          </SelectTrigger>
          <SelectContent className="bg-[var(--surface-elevated)] max-h-[400px]">
            {verses.map((v) => (
              <SelectItem key={v.verse} value={String(v.verse)}>{lang === "es" ? "Versículo" : "Verse"} {v.verse}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 ml-auto shrink-0">
          <Button
            variant="ghost"
            size="sm"
            data-testid="compare-toggle-btn"
            onClick={() => setCompareOpen((v) => !v)}
            className={`rounded-full px-2.5 md:px-3 h-9 text-xs uppercase tracking-widest transition-colors duration-200 ${
              compareOpen ? "bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90 hover:text-white" : "text-[var(--text-secondary)]"
            }`}
          >
            {compareOpen ? (
              <X className="w-3.5 h-3.5 md:mr-1.5" strokeWidth={1.5} />
            ) : (
              <Columns2 className="w-3.5 h-3.5 md:mr-1.5" strokeWidth={1.5} />
            )}
            <span className="hidden md:inline">{lang === "es" ? "Comparar" : "Compare"}</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            data-testid="prev-chapter-btn"
            disabled={chapter <= 1}
            onClick={() => setChapter((c) => Math.max(1, c - 1))}
            className="rounded-full hidden md:inline-flex"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            data-testid="next-chapter-btn"
            disabled={chapter >= totalChapters}
            onClick={() => setChapter((c) => Math.min(totalChapters, c + 1))}
            className="rounded-full hidden md:inline-flex"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
      </div>

      <div className={compareOpen ? "" : "max-w-3xl"}>
        <div className={compareOpen ? "grid md:grid-cols-2 gap-8" : ""}>
          {/* Primary column */}
          <div>
            <div className="flex items-center justify-between">
              <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">
                {version}
              </p>
              {!compareOpen && (
                <button
                  data-testid="book-intro-btn"
                  onClick={() => { setStudyVerse(null); setStudyOpen(true); }}
                  className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border border-[var(--border)] hover:bg-[var(--surface)] transition-colors duration-200 flex items-center gap-1.5 text-[var(--text-secondary)]"
                >
                  <Info className="w-3 h-3" strokeWidth={1.5} />
                  {lang === "es" ? "Sobre este libro" : "About this book"}
                </button>
              )}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2" data-testid="chapter-heading">
              {currentBook?.name || ""} {chapter}
            </h1>

            <AudioBiblePlayer
              translation={version}
              book={bookId}
              chapter={chapter}
              lang={lang}
            />

            <div className="mt-10 space-y-5" data-testid="verses-container">
          {loading && <p className="text-[var(--text-secondary)]">{t("loading", lang)}</p>}
          {!loading && verses.map((v) => {
            const isBM = bookmarkedSet.has(bookmarkKey(v.verse));
            const isHighlighted = highlightVerse === v.verse;
            return (
              <div
                key={v.verse}
                data-testid={`verse-${v.verse}`}
                className={`group verse-block ${isHighlighted ? "verse-highlighted" : ""}`}
              >
                <div className="flex gap-4">
                  <span className="verse-number">{v.verse}</span>
                  <p className="verse-text font-serif-body text-lg leading-loose">{v.text}</p>
                </div>
                <div className="mt-2 ml-9 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    data-testid={`bookmark-verse-${v.verse}`}
                    onClick={() => toggleBookmark(v)}
                    style={{ fontSize: "13px" }}
                    className={`uppercase tracking-widest px-3 py-1 rounded-full border border-[var(--border)] transition-colors duration-200 ${isBM ? "bg-[var(--accent)] text-white border-transparent" : "hover:bg-[var(--surface)]"}`}
                  >
                    <Bookmark className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                    {isBM ? t("bookmarked", lang) : t("bookmark", lang)}
                  </button>
                  <button
                    data-testid={`reflect-verse-${v.verse}`}
                    onClick={() => openReflection(v)}
                    style={{ fontSize: "13px" }}
                    className="uppercase tracking-widest px-3 py-1 rounded-full border border-[var(--border)] hover:bg-[var(--surface)] transition-colors duration-200"
                  >
                    <MessageCircle className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                    {t("reflectHere", lang)}
                  </button>
                  <button
                    data-testid={`study-verse-${v.verse}`}
                    onClick={() => { setStudyVerse(v); setStudyOpen(true); }}
                    style={{ fontSize: "13px" }}
                    className="uppercase tracking-widest px-3 py-1 rounded-full border border-[var(--accent)]/50 text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white transition-colors duration-200"
                  >
                    <Sparkles className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                    {lang === "es" ? "Estudiar" : "Study"}
                  </button>
                </div>
              </div>
            );
          })}
          {!loading && version === "NIV" && verses.length > 0 && (
            <p data-testid="niv-copyright" className="pt-6 text-xs text-[var(--text-secondary)] leading-relaxed">
              The Holy Bible, New International Version® NIV® Copyright © 1973, 1978, 1984, 2011 by Biblica, Inc.® Used by Permission of Biblica, Inc.® All rights reserved worldwide.
            </p>
          )}
        </div>
          </div>

          {/* Compare column */}
          {compareOpen && (
            <div data-testid="compare-column">
              <div className="flex items-center gap-2">
                <Select value={compareVersion} onValueChange={setCompareVersion}>
                  <SelectTrigger
                    data-testid="compare-version-select"
                    className="w-[180px] h-9 bg-[var(--surface)] border-[var(--border)] rounded-full text-xs uppercase tracking-widest"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--surface-elevated)]">
                    {["KJV", "WEB", "NIV", "NLT", "RV1960", "RV2004", "NTV", "PDT", "LBLA"]
                      .filter((c) => c !== version)
                      .map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-2 opacity-70">
                {currentBook?.name || ""} {chapter}
              </h2>

              <div className="mt-10 space-y-5">
                {compareLoading && <p className="text-[var(--text-secondary)]">{t("loading", lang)}</p>}
                {!compareLoading && compareVerses.map((cv) => {
                  const isHighlighted = highlightVerse === cv.verse;
                  return (
                    <div
                      key={cv.verse}
                      data-testid={`compare-verse-${cv.verse}`}
                      className={`verse-block ${isHighlighted ? "verse-highlighted" : ""}`}
                    >
                      <div className="flex gap-4">
                        <span className="verse-number">{cv.verse}</span>
                        <p className="verse-text font-serif-body text-lg leading-loose text-[var(--text-primary)]/85">
                          {cv.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* End-of-chapter navigation — prev / next */}
      {!loading && verses.length > 0 && (
        <div
          data-testid="chapter-end-nav"
          className="mt-16 pt-8 border-t border-[var(--border)] max-w-3xl grid grid-cols-2 gap-3"
        >
          <button
            data-testid="chapter-end-prev"
            disabled={chapter <= 1}
            onClick={() => setChapter((c) => Math.max(1, c - 1))}
            className="group flex items-center gap-3 p-4 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] hover:border-[var(--accent)] transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed text-left"
          >
            <ChevronLeft className="w-5 h-5 text-[var(--accent)] flex-shrink-0" strokeWidth={1.5} />
            <div className="min-w-0">
              <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)]">
                {lang === "es" ? "Anterior" : "Previous"}
              </p>
              <p className="text-sm font-serif-body truncate">
                {chapter > 1 ? `${currentBook?.name || ""} ${chapter - 1}` : "—"}
              </p>
            </div>
          </button>
          <button
            data-testid="chapter-end-next"
            disabled={chapter >= totalChapters}
            onClick={() => setChapter((c) => Math.min(totalChapters, c + 1))}
            className="group flex items-center gap-3 p-4 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] hover:border-[var(--accent)] transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed text-right justify-end"
          >
            <div className="min-w-0">
              <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)]">
                {lang === "es" ? "Siguiente" : "Next"}
              </p>
              <p className="text-sm font-serif-body truncate">
                {chapter < totalChapters ? `${currentBook?.name || ""} ${chapter + 1}` : "—"}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-[var(--accent)] flex-shrink-0" strokeWidth={1.5} />
          </button>
        </div>
      )}

      {/* Reading plan CTA — floating pill anchored above the bottom dock */}
      {planId && planDay > 0 && planInfo && (
        <div
          data-testid="plan-day-cta"
          className="fixed left-1/2 -translate-x-1/2 z-40 pointer-events-none"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)" }}
        >
          <div className="pointer-events-auto flex items-center gap-3 px-3 py-2 pr-2 rounded-full border border-[var(--border)] bg-[var(--surface-elevated)]/90 shadow-lg backdrop-blur-xl">
            <div className="pl-2 pr-1 min-w-0">
              <p className="text-[9px] uppercase tracking-widest text-[var(--text-secondary)] leading-none">
                {lang === "es" ? "Plan" : "Plan"} · {lang === "es" ? "Día" : "Day"} {planDay}
              </p>
              <p className="text-xs font-medium mt-0.5 truncate max-w-[180px] md:max-w-[280px]">
                {lang === "es" ? planInfo.name_es : planInfo.name_en}
              </p>
            </div>
            <button
              data-testid="mark-day-complete-btn"
              onClick={markDayComplete}
              disabled={dayCompleted || markingDay}
              className={`h-10 px-4 rounded-full text-xs uppercase tracking-widest flex items-center gap-1.5 transition-colors duration-200 whitespace-nowrap ${
                dayCompleted
                  ? "bg-[var(--accent)] text-white cursor-default"
                  : "bg-[var(--text-primary)] text-[var(--bg)] hover:bg-[var(--accent)] hover:text-white disabled:opacity-60"
              }`}
            >
              {dayCompleted ? (
                <>
                  <Check className="w-3.5 h-3.5" strokeWidth={2} />
                  {lang === "es" ? "Completado" : "Completed"}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  {markingDay
                    ? (lang === "es" ? "Guardando…" : "Saving…")
                    : (lang === "es" ? "Marcar como leído" : "Mark as read")}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <Dialog open={reflectionOpen} onOpenChange={setReflectionOpen}>
        <DialogContent className="bg-[var(--surface-elevated)] border-[var(--border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {t("reflectingOn", lang)} {currentBook?.name} {chapter}:{selectedVerse?.verse}
            </DialogTitle>
          </DialogHeader>
          <blockquote className="border-l-2 border-[var(--accent)] pl-4 italic text-[var(--text-secondary)] font-serif-body">
            “{selectedVerse?.text}”
          </blockquote>
          <Textarea
            data-testid="reflection-textarea"
            value={reflectionText}
            onChange={(e) => setReflectionText(e.target.value)}
            placeholder={t("reflectionPlaceholder", lang)}
            className="min-h-[140px] bg-[var(--bg)] border-[var(--border)]"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setReflectionOpen(false)}
              data-testid="reflection-cancel"
              className="rounded-full"
            >
              {t("cancel", lang)}
            </Button>
            <Button
              data-testid="reflection-submit"
              onClick={submitReflection}
              disabled={posting || !reflectionText.trim()}
              className="rounded-full bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-white"
            >
              {t("post", lang)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <StudyPanel
        open={studyOpen}
        onOpenChange={setStudyOpen}
        verse={studyVerse}
        bookId={bookId}
        bookName={currentBook?.name || ""}
        chapter={chapter}
        translation={version}
        onOpenRef={async (ref) => {
          // Parse "Book Ch:V" (e.g. "John 3:16", "1 John 4:7", "Juan 3:16")
          const m = ref.match(/^(.+?)\s+(\d+):(\d+)(?:[-–]\d+)?$/);
          if (!m) return;
          const [, name, ch, vs] = m;
          const book = books.find(
            (b) => b.name.toLowerCase() === name.toLowerCase().trim()
          );
          if (!book) {
            toast.error(lang === "es" ? "Referencia no encontrada" : "Reference not found");
            return;
          }
          setBookId(book.bookid);
          setChapter(parseInt(ch));
          setSearchParams({ b: String(book.bookid), c: ch, v: vs });
          setStudyOpen(false);
        }}
      />

    </div>
  );
}
